/**
 * Spec 191 — manage which regions a brand monitors (and which is home).
 *
 * Closes the Region-arc activation gap: the schema, capture, analysis, and
 * reporting all existed, but nothing let a user give a brand a second region —
 * so Per-Region Capture could never be turned on for a brand. This section,
 * hosted in the Edit Brand modal beside Tags/Sharing, lets a user pick from the
 * seeded region catalog and choose the home region.
 *
 * Mirrors BrandSharingSection's immediate-mutation idiom: GET on mount, PUT the
 * full set on each change, toast + onChange() so the caller refreshes. Exactly
 * one home region at all times; at least one monitored region at all times.
 *
 * Backend (spec 191):
 *   GET /api/v1/tenants/:tenantId/brands/:brandId/regions
 *   PUT /api/v1/tenants/:tenantId/brands/:brandId/regions  body { regions:[{code,is_home}] }
 */
import { useState, useEffect, useCallback } from 'react';
import { Icon, Spinner } from '../ui/Icon.js';
import { apiGet } from '../../lib/api.js';
import { apiPut } from '../../lib/api-mutations.js';
import { useToast } from '../../hooks/useToast.js';
import type {
  Brand,
  AvailableRegion,
  MonitoredRegion,
  BrandRegionsResponse,
} from '../../types/index.js';

interface BrandRegionsSectionProps {
  brand: Brand;
  /** Called after a region mutation so callers can refetch the list/context. */
  onChange: () => void;
}

export function BrandRegionsSection({ brand, onChange }: BrandRegionsSectionProps) {
  const { addToast } = useToast();
  const tenantId = brand.tenant_id;
  const [available, setAvailable] = useState<AvailableRegion[]>([]);
  const [monitored, setMonitored] = useState<MonitoredRegion[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<BrandRegionsResponse>(
        `/api/v1/tenants/${tenantId}/brands/${brand.id}/regions`,
      );
      setAvailable(res.data.available);
      setMonitored(res.data.monitored);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to load regions', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, brand.id, addToast]);

  useEffect(() => {
    void load();
  }, [load]);

  const isMonitored = (code: string) => monitored.some((m) => m.code === code);
  const isHome = (code: string) => monitored.some((m) => m.code === code && m.is_home);

  // Persist a new full set. Optimistic; reverts on error. Callers guarantee the
  // invariants (≥1 region, exactly one home) before calling.
  async function save(next: MonitoredRegion[]) {
    const prev = monitored;
    setMonitored(next);
    setSaving(true);
    try {
      const res = await apiPut<{ data: { monitored: MonitoredRegion[] } }>(
        `/api/v1/tenants/${tenantId}/brands/${brand.id}/regions`,
        { regions: next.map((m) => ({ code: m.code, is_home: m.is_home })) },
      );
      setMonitored(res.data.monitored);
      onChange();
    } catch (err) {
      setMonitored(prev);
      addToast(err instanceof Error ? err.message : 'Failed to update regions', 'error');
    } finally {
      setSaving(false);
    }
  }

  function toggleRegion(region: AvailableRegion) {
    if (isMonitored(region.code)) {
      // Removing. Keep at least one region.
      if (monitored.length === 1) {
        addToast('A brand must monitor at least one region', 'error');
        return;
      }
      const wasHome = isHome(region.code);
      let next = monitored.filter((m) => m.code !== region.code);
      // If we removed the home region, promote the first remaining to home.
      if (wasHome && next.length > 0 && !next.some((m) => m.is_home)) {
        next = next.map((m, i) => (i === 0 ? { ...m, is_home: true } : m));
      }
      void save(next);
    } else {
      // Adding. First region added becomes home; otherwise non-home.
      const next: MonitoredRegion[] = [
        ...monitored,
        {
          code: region.code,
          display_name: region.display_name,
          is_home: monitored.length === 0,
        },
      ];
      void save(next);
    }
  }

  function setHome(code: string) {
    if (isHome(code)) return;
    void save(monitored.map((m) => ({ ...m, is_home: m.code === code })));
  }

  return (
    <section data-testid="brand-regions-section" className="hub-section">
      <div className="hub-section__head">
        <h2>Monitored regions</h2>
      </div>
      <p className="text-xs text-[var(--hub-color-ink-subtle)]">
        Choose which regions this brand is monitored in. With two or more, GEO Observatory
        can capture AI answers per region — side by side, never blended. One
        region is the home region.
      </p>
      <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2">
        {loading ? (
          <div className="flex items-center gap-2 px-1 py-2 text-xs text-slate-500">
            <Spinner className="text-violet-500" /> Loading
            regions…
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
            {available.map((region) => {
              const checked = isMonitored(region.code);
              const home = isHome(region.code);
              return (
                <li
                  key={region.code}
                  className="flex items-center justify-between gap-3 px-2.5 py-2"
                >
                  <label className="flex flex-1 items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={saving}
                      onChange={() => toggleRegion(region)}
                      data-testid={`region-option-${region.code}`}
                      className="h-4 w-4 rounded-md border-gray-300 text-violet-600 focus:ring-violet-500"
                    />
                    <Icon name="bx-globe" className="flex-shrink-0 text-slate-400" />
                    <span className={`text-sm ${checked ? 'text-slate-800' : 'text-slate-500'}`}>
                      {region.display_name}{' '}
                      <span className="font-mono text-xs text-slate-400">
                        {region.code}
                      </span>
                    </span>
                  </label>
                  {checked &&
                    (home ? (
                      <span
                        className="flex flex-shrink-0 items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700"
                        data-testid={`region-home-${region.code}`}
                      >
                        <Icon name="bx-star" className="fill-violet-500 text-violet-500" />
                        Home
                      </span>
                    ) : (
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => setHome(region.code)}
                        data-testid={`region-home-${region.code}`}
                        className="flex flex-shrink-0 items-center gap-1 rounded-full border border-slate-200 px-2 py-0.5 text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-slate-700"
                      >
                        <Icon name="bx-star" /> Set home
                      </button>
                    ))}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
